import * as PIXI from "pixi.js-legacy";
import LoadingScreen from "../../loader/LoadingScreen";
import SystemConfig, {CONFIG_PARAMS} from "../../system/SystemConfig";
import BaseLoader from "../../loader/BaseLoader";
import {inject, injectable} from "inversify";
import SERVICE_IDENTIFIER from "../../constants/identifiers";
import FontPreloader from "../../loader/FontPreloader";
import IApp from "../IApp";
import System from "../../system/System";
import PixiLayers from "./PixiLayers";
import IAudio from "../IAudio";
import IDOM from "../../ui/IDOM";
import * as MV from "../madnes/madnes_variables";
import {getUrl} from "../madnes/madnes";
import {isLocalhost} from "../../helpers";

@injectable()
class PixiLoader extends BaseLoader {

    protected _gameset: any;
    protected _app: IApp;
    protected _system: System;
    protected _layers: PixiLayers;
    protected _audio: IAudio;
    protected _config: SystemConfig;
    // @ts-ignore
    protected  _loader: Pixi.Loader;
    protected enablePartialProgressMiddleware: boolean = true

    constructor(
        @inject(SERVICE_IDENTIFIER.FontPreloader) fontPreloader: FontPreloader,
        @inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
        @inject(SERVICE_IDENTIFIER.EngineApp) app: IApp,
        @inject(SERVICE_IDENTIFIER.System) system: System,
        @inject(SERVICE_IDENTIFIER.EngineAppLayers) layers: PixiLayers,
        @inject(SERVICE_IDENTIFIER.EngineAudio) audio: IAudio,
        @inject(SERVICE_IDENTIFIER.LoadingScreen) loadingScreen: LoadingScreen,
        @inject(SERVICE_IDENTIFIER.DOM) dom: IDOM
    ) {
        //@ts-ignore
        PIXI.LoaderResource.prototype._loadElement = function (type) {
			if (this.metadata.loadElement) {
				this.data = this.metadata.loadElement
			} else if (type === 'image' && typeof globalThis.Image !== 'undefined') {
				this.data = new Image()
			} else {
				this.data = document.createElement(type)
			}
			if (this.crossOrigin) {
				this.data.crossOrigin = this.crossOrigin
			}
			if (!this.metadata.skipSource) {
				// Use fetch to track download progress
				fetch(this.url)
					.then((response) => {
						const contentLength = response.headers.get('content-length')
						if (!contentLength) {
							// Handle no content-length
							this.data.src = this.url
							return
						}
						const reader = response.body.getReader()
						const totalBytes = parseInt(contentLength, 10)
						let loadedBytes = 0

						const processChunk = ({ done, value }) => {
							if (done) {
								this.data.src = this.url // Once finished, set image src
								return
							}

							loadedBytes += value.length
							const progress = (loadedBytes / totalBytes) * 100

							// Dispatch custom progress event
							const progressEvent = new Event('progress')
							progressEvent.loaded = loadedBytes
							progressEvent.total = totalBytes
							progressEvent.progress = progress
							progressEvent.lengthComputable = true // Add lengthComputable
							progressEvent.url = this.url

							this.data.dispatchEvent(progressEvent)

							reader.read().then(processChunk)
						}
						reader.read().then(processChunk)
					})
					.catch((error) => {
						console.error('Error loading image:', error)
						this._boundOnError(error)
					})
			}

			this.data.addEventListener('error', this._boundOnError, false)
			this.data.addEventListener('load', this._boundComplete, false)
			this.data.addEventListener('progress', this._boundOnProgress, false)
			if (this.timeout) {
				this._elementTimer = setTimeout(this._boundOnTimeout, this.timeout)
			}
		}

        super(loadingScreen, fontPreloader, dom);

        this._gameset = config.gameset;
        this._app = app;
        this._system = system;
        // this._engine = engine;
        this._layers = layers;
        this._audio = audio;
        this._config = config;

        this._loader = new PIXI.Loader();
    }

    /**
     * @return {Promise<any>}
     */
    async preload(): Promise<any> {

        super.preload();

        return new Promise<void>(res => {

            const loader = new PIXI.Loader();

            if(this._gameset.preloader) {

                if(this._gameset.preloader.sprites) {
                    loader.add(this._gameset.preloader.sprites);
                }

                if(this._gameset.preloader.assets) {
                    for (let asset of this._gameset.preloader.assets) {
                        loader.add(asset.name, asset.url);
                    }
                }
            }

            loader.onComplete.add((loader, resources) => {
                // this._loading = new Loading(this._gameset, layer, resources);
                this.loadingScreen.start();
                res();
            });

            loader.load();
        });
    }

    /**
     * @return {Promise<any>}
     */
    load() {
        let quality = this._system.config.get(CONFIG_PARAMS.QUALITY);

        return new Promise(res => {

            for (let sprite of this._gameset.game[quality].sprites) {
                this._loader.add(this.getURL(sprite));
            }

            for (let asset of this._gameset.game[quality].assets) {
                this._loader.add(asset.name, this.getURL(asset.url));
            }

            if (this._gameset.system && this._gameset.system.audio) {
                for (let track of this._gameset.system.audio.tracks) {
                    if (track.reference) continue;
                    this._loader.add(track.name, this.getURL(track.url));
                }
            }

            for (let track of this._gameset.game.audio.tracks) {
                if (track.reference) continue;
                this._loader.add(track.name, this.getURL(track.url));
            }

            const resourceProgressMap = new Map<
				string,
				{ perc: number; chunk: number }
			>()

            this._loader.pre((resource, next) => {
				if (!this.enablePartialProgressMiddleware) {
					next()
					return
				}

				const res = resource as PIXI.LoaderResource

				resourceProgressMap.set(res.name, { perc: 0, chunk: res.progressChunk })

				res.onProgress.add((res, perc) => {
					resourceProgressMap.set(res.name, {
						perc: perc,
						chunk: res.progressChunk,
					})

					let totalProgress = 0
					resourceProgressMap.forEach((value) => {
						totalProgress += (value.perc / 100) * value.chunk
					})

					this.loadingScreen.updateMultiple(
						'framework',
						totalProgress * 100,
						0.9
					)
				})

				next()
			})

            this._loader.onComplete.add((load, resources) => {
                this.loadingScreen.updateMultiple('framework', 100, 0.9)
                this._loader.onProgress.detachAll()
				this._loader.onComplete.detachAll()
                this.enablePartialProgressMiddleware = false
                res(resources);
            });

            this._loader.load();
        });
    }

    loadComplete(data) {
        this._app.loadComplete();
        this._audio.loadComplete(data);
    }

    getURL(url) {
        if (this._config.get(CONFIG_PARAMS.URL_CDN) && !isLocalhost()) {
            url = url.replace("assets/","");
            if (url.includes("framework")) {
                return this._config.get(CONFIG_PARAMS.URL_CDN) + url;
            } else {
                return this._config.get(CONFIG_PARAMS.URL_CDN) + this.getGameId() + url;
            }
        } else {
            return url;
        }
    }

    getGameId() {
        return this._config.get(CONFIG_PARAMS.GAME_ID) + "/";
    }
}

export default PixiLoader;
